import { Box, Text, Flex, useMantineTheme, Center, Grid } from "@mantine/core";
import { InterestContext } from "../../context/auth";
import { formatCurrency } from "../../utils/currency";
import type { IProductPlan } from "../../interfaces/Products/IProductDetail";
import { TickCircle } from "iconsax-react";
import { useFormContext } from "react-hook-form";
import { IPostBuyNow } from "../../interfaces/PurchaseNow";
import { useTranslation } from "react-i18next";

interface IPlanSelectorProps {
  plan: IProductPlan;
  onSelect: () => void;
  isSelected: boolean;
}

const PlanSelector = ({ plan, onSelect, isSelected }: IPlanSelectorProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  return (
    <Flex
      onClick={onSelect}
      align={"center"}
      p={"12px"}
      gap={"8px"}
      justify={"space-between"}
      sx={(theme) => ({
        backgroundColor: isSelected ? theme.colors.blue[0] : "#fff",
        border: isSelected
          ? `1.5px solid ${theme.colors.blue[6]}`
          : `1px solid ${theme.colors.gray[3]}`,
        borderRadius: "8px",
        flex: 1,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: theme.colors.blue[6],
          backgroundColor: theme.colors.blue[0],
        },
      })}
    >
      <Flex align={"center"} gap="8px">
        {isSelected ? (
          <TickCircle
            size="28"
            color={theme.colors.primary[2]}
            variant="Bold"
          />
        ) : (
          <Center h={"28px"} w={"28px"}>
            <Box
              h={"21px"}
              w={"21px"}
              bg="#fff"
              style={{ borderRadius: "50%", border: "2px solid #6B6B6B" }}
            />
          </Center>
        )}

        <Flex direction="column" justify="center">
          <Text
            color={theme.colors.basic[5]}
            fw={600}
            fz="16px"
            style={{ whiteSpace: "nowrap" }}
          >
            {plan.name}
          </Text>

          {plan.qty_contract > 1 ? (
            <Text
              color={theme.colors.basic[4]}
              fw={500}
              fz="12px"
              style={{ whiteSpace: "nowrap" }}
            >
              {t("marketplace.comprar_agora.purchase_step_2.limit", {
                qty_contract: plan.qty_contract,
              })}
            </Text>
          ) : (
            <Text
              color={theme.colors.basic[4]}
              fw={500}
              fz="12px"
              style={{ whiteSpace: "nowrap" }}
            >
              {t("marketplace.comprar_agora.purchase_step_2.ilimited")}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex direction="column" justify="center">
        {plan.qty_of_installments > 0 ? (
          <Text
            color={theme.colors.basic[4]}
            fw={500}
            fz="12px"
            style={{ whiteSpace: "nowrap" }}
          >
            {plan.qty_of_installments}
            {t("marketplace.comprar_agora.purchase_step_2.x_of")}
          </Text>
        ) : null}
        <Text color={theme.colors.basic[5]} fw={700} fz="20px">
          {plan.qty_of_installments > 0
            ? formatCurrency(plan.price / plan.qty_of_installments)
            : formatCurrency(plan.price)}
        </Text>
      </Flex>
    </Flex>
  );
};

interface IProps {
  product: InterestContext;
}

const ProductPlans = ({ product }: IProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { setValue, watch } = useFormContext<IPostBuyNow>();
  const selectedPlan = watch("plan_id");

  const handlePlanSelect = (plan: IProductPlan) => {
    setValue("plan_id", plan.id);
  };

  return (
    <Flex direction={"column"} gap="8px">
      <Flex gap="8px" w="100%" justify="space-between">
        <Text color={theme.colors.basic[5]} fw={600} fz="14px">
          {t("marketplace.comprar_agora.purchase_step_2.plan")}
        </Text>
        <Text color={theme.colors.alerts[0]} fw={600} fz="16px">
          *
        </Text>
      </Flex>

      <Grid w="100%" grow gutter={"16px"}>
        {product.plan.map((plan) => (
          <Grid.Col span={4} key={plan.id}>
            <PlanSelector
              plan={plan}
              isSelected={selectedPlan === plan.id}
              onSelect={() => handlePlanSelect(plan)}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Flex>
  );
};

export default ProductPlans;
